import { useEffect, useState } from "react";

const AddressAutoCompleteCore = ({
  onSelectAddress,
  address,
  setAddress,
  error,
  countryCode,
  stateName,
  cityName, // 👈 Added city name prop
}: {
  onSelectAddress: (data: any) => void;
  address: string;
  setAddress: (address: string) => void;
  error?: string | null;
  countryCode: string;
  stateName?: string;
  cityName?: string; // 👈 Optional or required based on your form workflow
}) => {
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [sessionToken, setSessionToken] = useState<any>(null);

  useEffect(() => {
    if (window.google?.maps?.places?.AutocompleteSessionToken) {
      setSessionToken(new google.maps.places.AutocompleteSessionToken());
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchPredictions = async () => {
      // Require at least a state/city or keep it open if not provided yet
      if (!address.trim() || !window.google?.maps?.places) {
        setSuggestions([]);
        return;
      }

      try {
        // Build query string context dynamically with city and state if available
        let queryInput = address;
        if (
          cityName &&
          !queryInput.toLowerCase().includes(cityName.toLowerCase())
        ) {
          queryInput = `${queryInput}, ${cityName}`;
        }
        if (
          stateName &&
          !queryInput.toLowerCase().includes(stateName.toLowerCase())
        ) {
          queryInput = `${queryInput}, ${stateName}`;
        }

        const { suggestions: rawSuggestions } =
          await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
            {
              input: queryInput,
              includedRegionCodes: [countryCode.toLowerCase()],
              sessionToken: sessionToken,
            },
          );

        if (isMounted && rawSuggestions) {
          // STRICT FILTER: Filter out any suggestion that doesn't match the selected state/city if they are provided
          const filtered = rawSuggestions.filter((item: any) => {
            const text = (item.placePrediction?.text?.text || "").toLowerCase();
            const matchesState = stateName
              ? text.includes(stateName.toLowerCase())
              : true;
            const matchesCity = cityName
              ? text.includes(cityName.toLowerCase())
              : true;
            return matchesState && matchesCity;
          });

          setSuggestions(filtered);
        }
      } catch (error) {
        console.error("Autocomplete error:", error);
      }
    };

    const timer = setTimeout(fetchPredictions, 300);
    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [address, countryCode, stateName, cityName, sessionToken]);

  const handleSelect = async (prediction: any) => {
    const placePrediction = prediction.placePrediction;
    const description = placePrediction.text.text;

    setAddress(description);
    setSuggestions([]);

    try {
      const place = placePrediction.toPlace();
      await place.fetchFields({
        fields: ["addressComponents", "formattedAddress"],
      });

      const addressComponents = place.addressComponents || [];

      let streetNumber = "";
      let route = "";
      let city = "";
      let province = "";
      let postalCode = "";

      addressComponents.forEach((component: any) => {
        const types = component.types;
        if (types.includes("street_number")) streetNumber = component.longText;
        if (types.includes("route")) route = component.longText;
        if (types.includes("locality")) city = component.longText;
        if (types.includes("administrative_area_level_1"))
          province = component.shortText;
        if (types.includes("postal_code")) postalCode = component.longText;
      });

      onSelectAddress({
        formattedAddress: place.formattedAddress || description,
        street: `${streetNumber} ${route}`.trim(),
        city: city || cityName || "",
        province: province || stateName || "",
        postalCode,
      });

      if (window.google?.maps?.places?.AutocompleteSessionToken) {
        setSessionToken(new google.maps.places.AutocompleteSessionToken());
      }
    } catch (error) {
      console.error("Place details fetch error:", error);
    }
  };

  // Dynamic placeholder based on context
  let placeholderText = "Search street address...";
  if (cityName && stateName) {
    placeholderText = `Search street in ${cityName}, ${stateName}...`;
  } else if (stateName) {
    placeholderText = `Search street in ${stateName}...`;
  }

  const [showOptions, setShowOptions] = useState(false);

  return (
    <div
      className="relative w-full max-w-full"
      onBlur={() => {
        setTimeout(() => {
          setShowOptions(false);
        }, 1000);
      }}
      onFocus={() => setShowOptions(true)}
    >
      <input
        value={address}
        onChange={(e) => setAddress(e.target.value)}
        placeholder={placeholderText}
        className={`w-full border p-2 rounded-md ${error ? "!border-error" : ""} active:outline-primary focus:outline-primary`}
      />
      {suggestions.length > 0 && showOptions && (
        <ul className="absolute z-10 w-full bg-white border rounded-md shadow-lg mt-1 max-h-60 overflow-auto">
          {suggestions.map((suggestion, index) => {
            const mainText = suggestion.placePrediction.text.text;
            return (
              <li
                key={index}
                onClick={() => handleSelect(suggestion)}
                className="px-4 py-2 cursor-pointer hover:bg-gray-100 text-sm text-gray-700"
              >
                {mainText}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default AddressAutoCompleteCore;
